import { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { searchMulti } from '../services/tmdb';
import { useStore } from '../store/useStore';
import MediaCard from '../components/media/MediaCard';
import LoadingSkeleton from '../components/ui/LoadingSkeleton';
import MatrixLink from '../components/ui/MatrixLink';
import type { SearchResult } from '../types';

const QUICK_SEARCHES = [
  'chosen family anime',
  'movies that break the loop',
  'comfort movies for a reset',
  'superhero team movies',
  'mind bending sci-fi',
  'date night reality shows',
];

function getResultTitle(result: SearchResult) {
  return result.title || result.name || 'Untitled';
}

function getSearchSignal(query: string, count: number) {
  const lowerQuery = query.toLowerCase();

  if (!query) {
    return {
      label: 'Open signal',
      body: 'Search by title, feeling, world, actor, or pattern. This is the app listening before it recommends.',
    };
  }

  if (count === 0) {
    return {
      label: 'Signal needs tuning',
      body: 'No clean matches yet. Try a simpler title, a genre, or ask Discover for a body scan instead.',
    };
  }

  if (lowerQuery.includes('family') || lowerQuery.includes('team') || lowerQuery.includes('crew')) {
    return {
      label: 'Many bodies, one body',
      body: 'This search is pulling toward crews, families, teams, and people becoming stronger together.',
    };
  }

  if (lowerQuery.includes('loop') || lowerQuery.includes('mind') || lowerQuery.includes('sci-fi')) {
    return {
      label: 'Pattern scan',
      body: 'This search is looking for systems, hidden rules, and stories that bend the frame.',
    };
  }

  if (lowerQuery.includes('date') || lowerQuery.includes('romance') || lowerQuery.includes('love')) {
    return {
      label: 'Connection scan',
      body: 'This search is tuned for shared attention: easy entry, emotional signal, and something to talk about after.',
    };
  }

  return {
    label: 'Title scan',
    body: 'CineVault found matching bodies in the archive. Pick one and let the rain open the door.',
  };
}

export default function Search() {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const queryResetRef = useRef(false);
  const { setSearchQuery } = useStore();

  useEffect(() => {
    queryResetRef.current = true;
    setPage(1);
    setResults([]);
    setTotalPages(0);
    setError(null);
  }, [query]);

  useEffect(() => {
    let ignore = false;

    const fetchResults = async () => {
      if (!query) {
        setResults([]);
        setLoading(false);
        queryResetRef.current = false;
        return;
      }

      if (queryResetRef.current && page !== 1) {
        return;
      }

      queryResetRef.current = false;

      try {
        setLoading(true);
        setError(null);
        const data = await searchMulti(query, page);
        if (ignore) return;

        setResults(prev => page === 1 ? data.results : [...prev, ...data.results]);
        setTotalPages(Math.min(data.total_pages, 500));
        setSearchQuery(query);
      } catch (error) {
        console.error('Error searching:', error);
        if (!ignore) {
          setError('Search hit turbulence. Try again or use Discover for a guided scan.');
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    fetchResults();

    return () => {
      ignore = true;
    };
  }, [query, page, setSearchQuery]);

  // Filter out people and keep only movies and TV shows
  const mediaResults = useMemo(
    () => results.filter(r => r.media_type === 'movie' || r.media_type === 'tv'),
    [results]
  );
  const searchSignal = getSearchSignal(query, mediaResults.length);
  const topMatches = mediaResults.slice(0, 3).map(getResultTitle);

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,rgba(16,185,129,0.16),transparent_30%),radial-gradient(circle_at_top_right,rgba(201,168,76,0.12),transparent_26%),#151210] px-4 pb-12 sm:px-6 lg:px-12">
      <div className="max-w-7xl mx-auto">
        {/* Search Header */}
        <div className="mb-8 overflow-hidden rounded-3xl border border-emerald-300/15 bg-black/30 p-5 shadow-2xl shadow-black/30 backdrop-blur-md sm:p-7">
          <div className="grid gap-5 lg:grid-cols-[1.4fr_0.8fr] lg:items-end">
            <div>
              <p className="mb-3 text-xs font-bold uppercase tracking-[0.32em] text-emerald-200/80">Archive Sense</p>
              <h1 className="text-3xl font-black tracking-tight text-white sm:text-5xl" style={{ fontFamily: "'Playfair Display', serif" }}>
                {query ? `Search: ${query}` : 'Search the vault by signal'}
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-gray-300 sm:text-base">
                {query
                  ? `${mediaResults.length} playable match${mediaResults.length !== 1 ? 'es' : ''} found so far.`
                  : 'Type a title, actor, genre, mood, or pattern. If you do not know the exact title, search the feeling.'}
              </p>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-amber-200/80">{searchSignal.label}</p>
              <p className="mt-2 text-sm leading-relaxed text-gray-300">{searchSignal.body}</p>
              {topMatches.length > 0 && (
                <p className="mt-3 text-xs text-emerald-100/80">
                  Strong first signals: {topMatches.join(', ')}
                </p>
              )}
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            {QUICK_SEARCHES.map((suggestion) => (
              <MatrixLink
                key={suggestion}
                to={`/search?q=${encodeURIComponent(suggestion)}`}
                titleForTransition={suggestion}
                className="rounded-full border border-white/10 bg-white/[0.06] px-3 py-1.5 text-xs text-gray-200 transition-colors hover:border-emerald-300/40 hover:bg-emerald-300/10"
              >
                {suggestion}
              </MatrixLink>
            ))}
          </div>
        </div>

        {/* Results Grid */}
        {error && (
          <div className="mb-6 rounded-2xl border border-red-300/20 bg-red-950/30 p-4 text-sm text-red-100">
            {error}
          </div>
        )}

        {loading && page === 1 ? (
          <LoadingSkeleton type="card" count={12} />
        ) : query && mediaResults.length === 0 ? (
          <div className="rounded-3xl border border-white/10 bg-black/25 p-8 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full border border-emerald-300/20 bg-emerald-300/10 text-3xl">
              ◌
            </div>
            <h2 className="text-2xl font-bold text-white">No clean match yet</h2>
            <p className="mx-auto mt-2 max-w-xl text-sm leading-relaxed text-gray-400">
              The vault did not find a playable movie or show for "{query}". Try a shorter title, search by actor, or let Discover translate the feeling into picks.
            </p>
            <div className="mt-5 flex flex-wrap justify-center gap-3">
              <MatrixLink to="/discover" titleForTransition="Discover" className="rounded-full bg-emerald-300 px-4 py-2 text-sm font-bold text-black transition-colors hover:bg-emerald-200">
                Ask Discover
              </MatrixLink>
              <MatrixLink to="/browse/movie" titleForTransition="Browse Movies" className="rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-white/20">
                Browse Movies
              </MatrixLink>
            </div>
          </div>
        ) : !query ? (
          <div className="rounded-3xl border border-white/10 bg-black/25 p-8 text-center">
            <svg className="mx-auto mb-4 h-14 w-14 text-emerald-200/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <h2 className="text-2xl font-bold text-white">Start with a signal</h2>
            <p className="mt-2 text-gray-400">Use the header search or one of the quick searches above.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {mediaResults.map((result) => (
                <MediaCard
                  key={`${result.media_type}-${result.id}`}
                  media={result}
                  type={result.media_type as 'movie' | 'tv'}
                />
              ))}
            </div>

            {/* Load More */}
            {page < totalPages && (
              <div className="flex justify-center mt-8">
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={loading}
                  className="px-6 py-3 bg-white/10 hover:bg-white/20 rounded-lg font-semibold transition-colors disabled:opacity-50"
                >
                  {loading ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
